/**
 * Global browser error reporter.
 *
 * Wires up window.onerror + unhandledrejection + axios error interceptor
 * and ships anything actionable to `POST /api/log/client-error`. Skips
 * known noise (ResizeObserver quirks, cross-origin script errors, browser
 * extension errors) BEFORE spending a network call. Coalesces repeat
 * events by fingerprint so a 30-error burst on one endpoint collapses
 * into ONE report with an occurrences counter (backend increments an
 * existing doc rather than inserting duplicates). Caps to 30 unique
 * reports per session and prefers `navigator.sendBeacon` so reports
 * survive page-unload.
 *
 * Category surface (must stay in sync with backend `classify_error`):
 *   - `network_error`         → axios rejection with NO response object
 *                                (offline, DNS, TLS, CORS pre-flight,
 *                                 ECONNABORTED timeout, ERR_CANCELED,
 *                                 rate-limit-exhausted after 3× 429)
 *   - `backend_exception`     → axios rejection with `status >= 500`
 *   - `unhandled_error`       → window.onerror
 *   - `unhandled_rejection`   → window unhandledrejection
 *   - `react_render_crash`    → ErrorBoundary (POSTs directly)
 * 401 is intentionally ignored — that's the normal expired-token flow.
 */
import api from "./api";

const SESSION_CAP = 30;
// Coalesce window: after an error fires we wait this many ms of quiet
// before flushing. Cap total buffer age too so a slow drip still gets
// reported eventually.
const COALESCE_QUIET_MS = 30_000;
const COALESCE_MAX_AGE_MS = 90_000;
// Max reports to buffer at once; keeps the map bounded.
const COALESCE_MAX_KEYS = 50;

const NOISE_PATTERNS = [
  /resizeobserver loop/i,
  /^script error\.?$/i,
  /java object is gone/i,
  /window\.webkit\.messagehandlers/i,
];
const NOISE_URL_PATTERNS = [
  /^chrome-extension:/i,
  /^moz-extension:/i,
  /^safari-web-extension:/i,
  /^iabjs:/i,
];

let reportCount = 0;
let lastHiddenAt = 0;
const pageLoadedAt = Date.now();
/** key -> { source, message, options, count, first_seen, last_seen, timer } */
const pending = new Map();

function apiRoot() {
  // Read env at call time so hot-reload picks up any change
  return process.env.REACT_APP_BACKEND_URL || "";
}

/** Stable client-side fingerprint. Mirrors the backend expectation:
 *  sha-ish string derived from (source, endpoint_path, first 200 chars of
 *  message). Not cryptographic — just a bucket key. */
function fingerprintKey(source, endpointPath, message) {
  const s = `${source}|${endpointPath || ""}|${(message || "").slice(0, 200)}`;
  let h1 = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h1 ^= s.charCodeAt(i);
    h1 = Math.imul(h1, 16777619);
  }
  // Return as unsigned hex, prefixed so it never collides with a message
  return `fp_${(h1 >>> 0).toString(16)}`;
}

function isNoise(message, sourceUrl) {
  if (!message) return true;
  for (const p of NOISE_PATTERNS) if (p.test(message)) return true;
  if (sourceUrl) {
    for (const p of NOISE_URL_PATTERNS) if (p.test(sourceUrl)) return true;
  }
  return false;
}

function deviceInfo() {
  try {
    const ua = navigator.userAgent || "";
    const browser = /Firefox/.test(ua)
      ? "Firefox"
      : /Chrome/.test(ua)
        ? "Chrome"
        : /Safari/.test(ua)
          ? "Safari"
          : /Edge/.test(ua)
            ? "Edge"
            : "Other";
    const os = /Windows/.test(ua)
      ? "Windows"
      : /Mac OS X|Macintosh/.test(ua)
        ? "macOS"
        : /Android/.test(ua)
          ? "Android"
          : /iPhone|iPad|iPod/.test(ua)
            ? "iOS"
            : /Linux/.test(ua)
              ? "Linux"
              : "Other";
    return {
      browser,
      os,
      pwa: window.matchMedia?.("(display-mode: standalone)")?.matches || false,
      device: /Mobi|Android|iPhone|iPad|iPod/.test(ua) ? "mobile" : "desktop",
      screen: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
    };
  } catch (_) {
    return {};
  }
}

/** Extract just the path portion of the axios request URL so the
 *  fingerprint bucket is per-endpoint (not per-query-string). */
function endpointPath(err) {
  const url = err?.config?.url || "";
  if (!url) return "";
  try {
    if (url.startsWith("/")) return url.split("?")[0];
    return new URL(url, window.location.origin).pathname;
  } catch (_) {
    return url;
  }
}

/**
 * Send one report to the backend. Uses sendBeacon during page-unload,
 * fetch keepalive otherwise. Silent no-op if noise, capped, or the
 * tab is currently backgrounded.
 */
function sendReport(source, message, options, occurrences, firstSeenAt) {
  try {
    if (
      document.visibilityState === "hidden" ||
      Date.now() - lastHiddenAt < 500
    ) {
      return;
    }
    if (isNoise(message, options?.sourceUrl)) return;
    if (reportCount >= SESSION_CAP) return;
    reportCount += 1;

    const body = JSON.stringify({
      source,
      message: (message || "").slice(0, 5000),
      stack: (options.stack || "").slice(0, 20000),
      device_info: deviceInfo(),
      context: options.context || {},
      fingerprint: options.fingerprint || "",
      occurrences: Math.max(1, occurrences | 0),
      first_seen_at: new Date(firstSeenAt || Date.now()).toISOString(),
    });

    const url = `${apiRoot()}/api/log/client-error`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* never let the reporter itself break */
  }
}

/** Add a fresh event into the coalesce buffer; schedule/refresh flush. */
function coalesce(source, message, options) {
  const key = options.fingerprint;
  if (!key) {
    // No fingerprint (shouldn't happen for interceptor calls) — send raw.
    sendReport(source, message, options, 1, Date.now());
    return;
  }
  const now = Date.now();
  const existing = pending.get(key);
  if (existing) {
    existing.count += 1;
    existing.last_seen = now;
    // Only refresh the debounce timer while we're still inside the max-age.
    if (now - existing.first_seen < COALESCE_MAX_AGE_MS) {
      clearTimeout(existing.timer);
      existing.timer = setTimeout(() => flush(key), COALESCE_QUIET_MS);
    }
    return;
  }
  if (pending.size >= COALESCE_MAX_KEYS) {
    // Evict the oldest to keep the map bounded.
    const oldest = [...pending.entries()].sort(
      (a, b) => a[1].first_seen - b[1].first_seen,
    )[0];
    if (oldest) {
      clearTimeout(oldest[1].timer);
      flush(oldest[0]);
    }
  }
  const entry = {
    source,
    message,
    options,
    count: 1,
    first_seen: now,
    last_seen: now,
    timer: setTimeout(() => flush(key), COALESCE_QUIET_MS),
  };
  pending.set(key, entry);
}

function flush(key) {
  const entry = pending.get(key);
  if (!entry) return;
  pending.delete(key);
  clearTimeout(entry.timer);
  sendReport(
    entry.source,
    entry.message,
    entry.options,
    entry.count,
    entry.first_seen,
  );
}

/** Flush every pending bucket immediately. Used on page-unload. */
function flushAll() {
  for (const key of [...pending.keys()]) flush(key);
}

/**
 * Report a client-side error. `options.stack` and `options.context` are
 * optional; the reporter fills in device info and coalesces automatically.
 * Silent no-op when noise or capped.
 */
export function reportClientError(source, message, options = {}) {
  const fp = options.fingerprint
    ? options.fingerprint
    : fingerprintKey(
        source,
        options.context?.url || options.context?.endpoint || "",
        message,
      );
  if (options.immediate) {
    // User-visible failures (e.g. route optimization) must show up in the
    // Errors tab right away — skip the 30s quiet-window coalescing. The
    // backend still collapses repeats by fingerprint.
    sendReport(source, message, { ...options, fingerprint: fp }, 1, Date.now());
    return;
  }
  coalesce(source, message, { ...options, fingerprint: fp });
}

/**
 * Install the global handlers. Idempotent — safe to call more than once.
 */
export function installErrorReporter() {
  if (typeof window === "undefined") return;
  if (window.__errorReporterInstalled) return;
  window.__errorReporterInstalled = true;

  window.addEventListener("error", (ev) => {
    reportClientError("unhandled_error", ev.message || String(ev.error) || "", {
      stack: (ev.error && ev.error.stack) || "",
      sourceUrl: ev.filename || "",
      context: {
        lineno: ev.lineno || 0,
        colno: ev.colno || 0,
        url: window.location.href,
      },
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason || {};
    const msg =
      typeof reason === "string" ? reason : reason.message || String(reason);
    reportClientError("unhandled_rejection", msg || "unhandled rejection", {
      stack: (reason && reason.stack) || "",
      context: { url: window.location.href },
    });
  });

  // Track PWA suspend so we can drop iOS network-abort bursts.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      lastHiddenAt = Date.now();
      // Flush any pending buckets before the tab goes cold — sendBeacon
      // is designed for exactly this transition.
      flushAll();
    }
  });
  // Also flush on hard-nav / close.
  window.addEventListener("pagehide", flushAll);

  // ------------------------------------------------------------------
  //  Axios interceptor: split into three buckets so the Errors tab can
  //  actually surface transient outages, not just 500s.
  //    · no response  → network_error   (offline, DNS, CORS, timeout,
  //                                       429 after retries)
  //    · status ≥ 500 → backend_exception
  //    · status 401   → skip (auth flow handles it)
  //    · everything else (4xx business errors) → skip
  // ------------------------------------------------------------------
  api.interceptors.response.use(
    (r) => r,
    (err) => {
      try {
        const status = err?.response?.status || 0;
        const path = endpointPath(err);
        const method = (err?.config?.method || "").toUpperCase();
        const retryCount = err?.config?.__retryCount || 0;
        const code = err?.code || "";
        const timeoutMs = err?.config?.timeout || 0;

        if (status === 401) return Promise.reject(err);

        if (!err.response) {
          // No response object at all — this is where "Network Error"
          // toasts actually come from. axios's err.code disambiguates:
          //   ERR_NETWORK    – offline / DNS / TLS / CORS pre-flight fail
          //   ECONNABORTED   – timeout expired (config.timeout or server)
          //   ERR_CANCELED   – deliberate AbortController cancel
          const rateLimitExhausted = retryCount >= 3;
          const online = typeof navigator !== "undefined" ? !!navigator.onLine : true;
          const kind = rateLimitExhausted
            ? "rate_limit_exhausted"
            : code === "ECONNABORTED"
              ? "timeout"
              : code === "ERR_CANCELED"
                ? "aborted"
                : !online
                  ? "offline"
                  : "unreachable";
          const summary =
            (err?.message || "Network error") +
            (path ? ` — ${method} ${path}` : "");
          reportClientError("network_error", summary, {
            context: {
              url: path,
              method,
              code: code || (err?.message || "").slice(0, 100),
              kind,
              timeout_ms: timeoutMs,
              retry_count: retryCount,
              online,
              age_since_page_load_ms: Date.now() - pageLoadedAt,
              status: 0,
            },
            fingerprint: fingerprintKey("network_error", path, kind),
          });
          return Promise.reject(err);
        }

        if (status >= 500) {
          reportClientError(
            "backend_exception",
            `${status} on ${method} ${path}`.slice(0, 500),
            {
              context: {
                status,
                url: path,
                method,
                data:
                  (err?.response?.data &&
                    JSON.stringify(err.response.data).slice(0, 500)) ||
                  "",
              },
              fingerprint: fingerprintKey(
                "backend_exception",
                `${method} ${path}`,
                String(status),
              ),
            },
          );
        }
      } catch (_) {
        /* swallow — reporter must never break the request path */
      }
      return Promise.reject(err);
    },
  );
}
